
import React, { useState, useRef } from 'react';
import { Film, Upload, Video, Sparkles, Play, ArrowRight, Wand2, RefreshCw, Check, AlertCircle, Settings2, Clapperboard, Move, ZoomIn, ScanEye, RotateCw } from 'lucide-react';
import Button from './Button';
import { generateCinematicVideo, generateConceptImage, generateMatchingEndFrame, editImageWithPrompt, planVideoScenes } from '../services/geminiService';
import { ImageSize, VeoModel, CinematicPlan } from '../types';
import { readFileAsBase64, getBase64Data, getMimeTypeFromBase64 } from '../utils/fileHelpers';

interface CinemaProps {
  onSave: (url: string, style: string) => void;
}

type Mode = 'motion' | 'transition';

const Cinema: React.FC<CinemaProps> = ({ onSave }) => {
  const [mode, setMode] = useState<Mode>('motion');
  const [model, setModel] = useState<VeoModel>(VeoModel.FAST);
  const [startImage, setStartImage] = useState<string | null>(null);
  const [endImage, setEndImage] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [generatedVideo, setGeneratedVideo] = useState<string | null>(null);
  
  // Loading states
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingFrame, setIsGeneratingFrame] = useState<'start' | 'end' | 'auto-end' | null>(null);
  const [isPlanning, setIsPlanning] = useState(false);
  
  // Data states
  const [error, setError] = useState<string | null>(null);
  const [suggestedPlans, setSuggestedPlans] = useState<CinematicPlan[]>([]);

  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const base64 = await readFileAsBase64(file);
        if (type === 'start') {
             setStartImage(base64);
             setSuggestedPlans([]); // Reset plans on new image
        }
        else setEndImage(base64);
        setGeneratedVideo(null); // Reset result on new input
        setError(null);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleGenerateFrame = async (type: 'start' | 'end') => {
     const framePrompt = window.prompt(`Describe the ${type} frame for your jewelry video:`);
     if (!framePrompt) return;

     setIsGeneratingFrame(type);
     try {
        const result = await generateConceptImage(framePrompt, ImageSize.SIZE_1K);
        if (type === 'start') {
            setStartImage(result);
            setSuggestedPlans([]);
        }
        else setEndImage(result);
        setGeneratedVideo(null);
     } catch (err) {
        alert("Failed to generate frame. Please try again.");
     } finally {
        setIsGeneratingFrame(null);
     }
  };

  const handlePlanScenes = async () => {
      if (!startImage) return;
      setIsPlanning(true);
      try {
          const startData = getBase64Data(startImage);
          const startMime = getMimeTypeFromBase64(startImage);
          const plans = await planVideoScenes(startData, startMime);
          setSuggestedPlans(plans);
      } catch (err) {
          console.error(err);
      } finally {
          setIsPlanning(false);
      }
  };

  const applyPlan = async (plan: CinematicPlan) => {
      // 1. Set the video prompt
      setPrompt(plan.description);
      
      // 2. Automatically generate the end frame based on the edit instruction
      setIsGeneratingFrame('auto-end');
      try {
          const startData = getBase64Data(startImage!);
          const startMime = getMimeTypeFromBase64(startImage!);
          
          const result = await editImageWithPrompt(startData, plan.editInstruction, startMime);
          setEndImage(result);
      } catch (err) {
          console.error(err);
          setError("Failed to create the end frame for this plan.");
      } finally {
          setIsGeneratingFrame(null);
      }
  };

  const handleAutoSuggestEndFrame = async () => {
      if (!startImage) {
          alert("Please upload a start frame first.");
          return;
      }

      setIsGeneratingFrame('auto-end');
      try {
          const startData = getBase64Data(startImage);
          const startMime = getMimeTypeFromBase64(startImage);
          
          const result = await generateMatchingEndFrame(startData, startMime);
          setEndImage(result);
      } catch (err) {
          console.error(err);
          alert("Failed to auto-generate end frame.");
      } finally {
          setIsGeneratingFrame(null);
      }
  };

  const handleGenerateVideo = async () => {
    if (!startImage || (mode === 'transition' && !endImage) || !prompt) {
        setError("Please provide all required images and a prompt.");
        return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedVideo(null);

    try {
        const startData = getBase64Data(startImage);
        const startMime = getMimeTypeFromBase64(startImage);
        
        let endData;
        let endMime;

        if (mode === 'transition' && endImage) {
            endData = getBase64Data(endImage);
            endMime = getMimeTypeFromBase64(endImage);
        }

        const videoUrl = await generateCinematicVideo(
            prompt,
            startData,
            startMime,
            model,
            endData,
            endMime
        );

        setGeneratedVideo(videoUrl);

    } catch (err) {
        console.error(err);
        setError("Video generation failed. Please verify your VITE_API_KEY in Vercel environment variables.");
    } finally {
        setIsGenerating(false);
    }
  };

  const getIcon = (type: string) => {
      switch(type) {
          case 'zoom': return <ZoomIn className="w-5 h-5" />;
          case 'orbit': return <RotateCw className="w-5 h-5" />;
          case 'pan': return <Move className="w-5 h-5" />;
          case 'focus': return <ScanEye className="w-5 h-5" />;
          default: return <Clapperboard className="w-5 h-5" />;
      }
  };
  
  const addAngleToPrompt = (angleText: string) => {
      setPrompt(prev => {
          const prefix = `Cinematic ${angleText} shot.`;
          return prev ? `${prefix} ${prev}` : prefix;
      });
  };

  return (
    <div className="pb-12 max-w-7xl mx-auto w-full">
       {/* Header */}
       <div className="mb-6 md:mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="min-w-0">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mb-2 md:mb-4 truncate">Lumina Cinema</h2>
          <p className="text-sm md:text-xl text-stone-500 font-light max-w-2xl leading-relaxed">
            Bring your jewelry to life with AI-generated motion.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
            <div className="relative group">
                <select 
                    value={model}
                    onChange={(e) => setModel(e.target.value as VeoModel)}
                    className="appearance-none bg-white border border-stone-200 text-stone-700 text-xs font-bold px-4 py-2 pr-8 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-stone-900 cursor-pointer transition-shadow hover:shadow-md"
                >
                    <option value={VeoModel.FAST}>VEO FAST</option>
                    <option value={VeoModel.QUALITY}>VEO PRO</option>
                </select>
                <Settings2 className="w-3 h-3 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none group-hover:text-stone-600" />
            </div>
            <div className="bg-amber-50 text-amber-900 px-3 py-2 rounded-full text-[10px] md:text-xs font-bold border border-amber-200 flex items-center shadow-sm">
                <Video className="w-3 h-3 mr-1.5" />
                VEO ENGINE
            </div>
        </div>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
         
         {/* Right Preview Panel */}
         <div className="lg:col-span-7 order-1 lg:order-2 w-full max-w-full">
            <div className="bg-[#1C1C1E] rounded-[2.5rem] w-full aspect-[9/16] md:aspect-[9/16] md:max-w-[420px] mx-auto lg:max-w-none lg:h-auto lg:min-h-[650px] flex items-center justify-center shadow-2xl shadow-stone-900/30 overflow-hidden relative border border-stone-800 transition-all duration-300">
                 {/* Cinematic Background */}
                 <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-stone-800 via-stone-950 to-black opacity-80"></div>
                 <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay"></div>
                 
                 {generatedVideo ? (
                    <div className="relative w-full h-full flex flex-col z-10">
                        <div className="flex-1 relative w-full h-full bg-black">
                             <video 
                                src={generatedVideo} 
                                controls 
                                autoPlay 
                                loop 
                                className="absolute inset-0 w-full h-full object-contain"
                             />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent backdrop-blur-sm flex justify-between items-center z-20">
                            <div>
                                <h4 className="text-white font-serif font-bold text-sm md:text-lg drop-shadow-md mb-0.5">Cinema Output</h4>
                                <span className="text-stone-300 text-[10px] md:text-xs uppercase tracking-wider drop-shadow-md font-bold">
                                    {model === VeoModel.QUALITY ? '1080p HQ' : '1080p Fast'} • 24fps
                                </span>
                            </div>
                            <Button variant="white" size="sm" onClick={() => onSave(generatedVideo!, mode)} className="shadow-lg">
                                <Check className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Save Asset</span>
                                <span className="md:hidden">Save</span>
                            </Button>
                        </div>
                    </div>
                 ) : (
                    <div className="text-stone-600 flex flex-col items-center relative z-10 text-center max-w-md p-6">
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-stone-700/50 flex items-center justify-center mb-6 bg-stone-800/50 backdrop-blur-sm shadow-xl">
                            <Film className="w-8 h-8 md:w-10 md:h-10 text-stone-500" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-serif text-stone-300 mb-2">Cinematic Preview</h3>
                        <p className="text-stone-500 mb-8 leading-relaxed text-sm">
                            {mode === 'motion' 
                                ? "Upload a product shot and describe the motion."
                                : "Upload a start and end frame for transitions."
                            }
                        </p>
                        
                        {isGenerating && (
                             <div className="flex flex-col items-center space-y-4 w-full">
                                <div className="w-48 h-1.5 bg-stone-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-stone-600 to-stone-400 animate-progress"></div>
                                </div>
                                <span className="text-[10px] md:text-xs text-stone-400 font-mono animate-pulse uppercase tracking-wider">Rendering with VEO...</span>
                             </div>
                        )}
                    </div>
                 )}
            </div>
         </div>

         {/* Left Control Panel */}
         <div className="lg:col-span-5 space-y-6 lg:space-y-8 order-2 lg:order-1 w-full max-w-full min-w-0">
            
            {/* Mode Switcher */}
            <div className="bg-white p-1.5 rounded-2xl border border-stone-200 flex shadow-sm">
                <button
                    onClick={() => { setMode('motion'); setGeneratedVideo(null); }}
                    className={`flex-1 flex items-center justify-center py-3 rounded-xl text-xs md:text-sm font-bold transition-all ${mode === 'motion' ? 'bg-stone-900 text-white shadow-md' : 'text-stone-400 hover:text-stone-900 hover:bg-stone-50'}`}
                >
                    <Play className="w-3 h-3 md:w-4 md:h-4 mr-2" /> Motion
                </button>
                <button
                    onClick={() => { setMode('transition'); setGeneratedVideo(null); }}
                    className={`flex-1 flex items-center justify-center py-3 rounded-xl text-xs md:text-sm font-bold transition-all ${mode === 'transition' ? 'bg-stone-900 text-white shadow-md' : 'text-stone-400 hover:text-stone-900 hover:bg-stone-50'}`}
                >
                    <ArrowRight className="w-3 h-3 md:w-4 md:h-4 mr-2" /> Transition
                </button>
            </div>

            {/* Upload Areas */}
            <div className="space-y-6">
                 {/* Start Frame */}
                 <div className="bg-white rounded-[2rem] border border-stone-100 overflow-hidden shadow-xl shadow-stone-200/40">
                    <div className="p-4 bg-stone-50 border-b border-stone-100 flex justify-between items-center">
                        <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest pl-1">Start Frame</span>
                        {startImage && <button onClick={() => setStartImage(null)} className="text-[10px] font-bold text-stone-400 hover:text-red-500 flex items-center uppercase tracking-wide transition-colors"><RefreshCw className="w-3 h-3 mr-1"/> Reset</button>}
                    </div>
                    <div className="p-6">
                        {startImage ? (
                            <img src={startImage} alt="Start" className="w-full h-40 object-contain rounded-2xl shadow-sm border border-stone-100" />
                        ) : (
                            <div className="h-40 border-2 border-dashed border-stone-200 rounded-2xl flex flex-col items-center justify-center bg-stone-50/30 hover:bg-stone-50 transition-colors">
                                <div className="flex gap-3">
                                    <Button size="sm" variant="outline" onClick={() => startInputRef.current?.click()} className="bg-white hover:bg-white shadow-sm">
                                        <Upload className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Upload</span>
                                        <span className="sm:hidden">Upload</span>
                                    </Button>
                                    <span className="text-stone-300 self-center text-[10px] font-bold">OR</span>
                                    <Button size="sm" variant="secondary" onClick={() => handleGenerateFrame('start')} isLoading={isGeneratingFrame === 'start'}>
                                        <Wand2 className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Create</span>
                                        <span className="sm:hidden">Create</span>
                                    </Button>
                                </div>
                                <input type="file" ref={startInputRef} hidden accept="image/*" onChange={(e) => handleFileChange(e, 'start')} />
                            </div>
                        )}
                    </div>
                 </div>

                 {/* Scene Planner */}
                 {mode === 'transition' && startImage && (
                    <div className="bg-stone-900 rounded-[2rem] p-6 text-white shadow-2xl shadow-stone-900/20 relative overflow-hidden">
                        <div className="absolute inset-0 bg-white/5 opacity-50"></div>
                        <div className="relative z-10">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h4 className="font-serif font-bold flex items-center gap-2 text-base">
                                        <Clapperboard className="w-4 h-4 text-amber-500" /> Scene Planner
                                    </h4>
                                </div>
                                <Button size="sm" variant="glassDark" onClick={handlePlanScenes} isLoading={isPlanning} className="text-[10px] py-1.5 px-3 h-8 uppercase tracking-wide font-bold">
                                    {isPlanning ? 'Analyzing...' : 'Auto-Plan Scene'}
                                </Button>
                            </div>
                            
                            {suggestedPlans.length > 0 && (
                                <div className="grid grid-cols-1 gap-2.5 animate-in slide-in-from-top-4">
                                    {suggestedPlans.map((plan, idx) => (
                                        <button 
                                            key={idx}
                                            onClick={() => applyPlan(plan)}
                                            className="text-left bg-white/5 hover:bg-white/10 border border-white/5 rounded-xl p-3 transition-colors flex items-start gap-4 group"
                                        >
                                            <div className="mt-0.5 p-2 bg-black/40 rounded-lg text-stone-400 group-hover:text-amber-400 transition-colors">
                                                {getIcon(plan.icon)}
                                            </div>
                                            <div>
                                                <span className="text-sm font-bold text-stone-200 block mb-0.5">{plan.title}</span>
                                                <span className="text-xs text-stone-400 leading-snug block line-clamp-2">{plan.description}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                 )}

                 {/* End Frame */}
                 {mode === 'transition' && (
                    <div className="relative pt-4">
                        <div className="absolute left-1/2 top-0 -translate-x-1/2 w-8 h-8 bg-stone-900 text-white rounded-full flex items-center justify-center z-10 shadow-lg border-2 border-white">
                            <ArrowRight className="w-4 h-4" />
                        </div>
                        <div className="bg-white rounded-[2rem] border border-stone-100 overflow-hidden shadow-xl shadow-stone-200/40">
                            <div className="p-4 bg-stone-50 border-b border-stone-100 flex justify-between items-center">
                                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest pl-1">End Frame</span>
                                {endImage && <button onClick={() => setEndImage(null)} className="text-[10px] font-bold text-stone-400 hover:text-red-500 flex items-center uppercase tracking-wide transition-colors"><RefreshCw className="w-3 h-3 mr-1"/> Reset</button>}
                            </div>
                            <div className="p-6">
                                {endImage ? (
                                    <img src={endImage} alt="End" className="w-full h-40 object-contain rounded-2xl shadow-sm border border-stone-100" />
                                ) : (
                                    <div className="h-40 border-2 border-dashed border-stone-200 rounded-2xl flex flex-col items-center justify-center bg-stone-50/30 hover:bg-stone-50 transition-colors">
                                        <div className="flex flex-col items-center gap-3">
                                            {/* Auto create button if NO plans are showing, otherwise rely on planner */}
                                            {startImage && suggestedPlans.length === 0 && (
                                                <Button size="sm" variant="primary" onClick={handleAutoSuggestEndFrame} isLoading={isGeneratingFrame === 'auto-end'} className="mb-1 w-full justify-center text-xs shadow-lg">
                                                    <Sparkles className="w-4 h-4 mr-2" /> Auto-Match Frame
                                                </Button>
                                            )}
                                            {isGeneratingFrame === 'auto-end' && suggestedPlans.length > 0 && (
                                                 <div className="text-[10px] text-stone-500 animate-pulse font-bold uppercase tracking-wider">Rendering...</div>
                                            )}

                                            <div className="flex gap-3">
                                                <Button size="sm" variant="outline" onClick={() => endInputRef.current?.click()} className="bg-white hover:bg-white shadow-sm">
                                                    <Upload className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Upload</span>
                                                    <span className="sm:hidden">Upload</span>
                                                </Button>
                                                <Button size="sm" variant="secondary" onClick={() => handleGenerateFrame('end')} isLoading={isGeneratingFrame === 'end'}>
                                                    <Wand2 className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Create</span>
                                                    <span className="sm:hidden">Create</span>
                                                </Button>
                                            </div>
                                        </div>
                                        <input type="file" ref={endInputRef} hidden accept="image/*" onChange={(e) => handleFileChange(e, 'end')} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                 )}
            </div>

            {/* Prompt Input */}
            <div className="bg-white p-6 rounded-[2rem] border border-stone-100 shadow-sm">
                <div className="flex justify-between items-center mb-4 px-1">
                     <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Animation Prompt</label>
                </div>
                
                {/* Angle Presets */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {['Front', 'Isometric', 'Top-Down', 'Low-Angle', 'Macro', 'Side'].map((angle) => (
                        <button
                            key={angle}
                            onClick={() => addAngleToPrompt(angle)}
                            className="px-3 py-1.5 bg-stone-50 hover:bg-stone-100 border border-stone-200 rounded-lg text-[10px] font-bold uppercase text-stone-500 transition-colors tracking-wide"
                        >
                            + {angle}
                        </button>
                    ))}
                </div>

                <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={mode === 'motion' 
                        ? "Describe movement... e.g. Camera orbits the ring slowly."
                        : "Describe the transition effect..."
                    }
                    className="w-full p-4 bg-stone-50 border border-stone-200 rounded-2xl focus:ring-2 focus:ring-stone-900 focus:outline-none resize-none h-24 mb-5 text-sm placeholder:text-stone-400 shadow-inner"
                />
                
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start text-red-600 text-xs font-medium">
                        <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        {error}
                    </div>
                )}

                <Button 
                    onClick={handleGenerateVideo} 
                    disabled={isGenerating || !startImage || (mode === 'transition' && !endImage) || !prompt}
                    isLoading={isGenerating}
                    className="w-full shadow-2xl shadow-stone-900/20 py-4"
                    size="lg"
                >
                    <Video className="w-5 h-5 mr-2" />
                    {isGenerating ? 'Rendering Cinematic...' : 'Generate Video'}
                </Button>
            </div>
         </div>
      </div>
    </div>
  );
};

export default Cinema;
